import { request } from "./http";

export interface ExamPart {
  code: string;
  teil: number;
  title: string;
  description: string;
  item_type: string;
  scored: boolean;
  timing_minutes: number;
  item_count: number;
  practiced: number;
  avg_best_score: number;
}

export interface ExamSection {
  code: string;
  name: string;
  english: string;
  icon: string;
  parts: ExamPart[];
}

export interface ExamOverview {
  board: string;
  level: string;
  name: string;
  sections: ExamSection[];
}

export interface ExamItemCard {
  id: string;
  slug: string;
  title: string;
  topic: string | null;
  image_url: string | null;
  practiced: boolean;
  best_score: number | null;
}

export interface PartItems {
  part: {
    code: string;
    title: string;
    description: string;
    item_type: string;
    scored: boolean;
    timing_minutes: number;
    strategy?: string;
  };
  items: ExamItemCard[];
}

export interface ExamItemDetail {
  id: string;
  board: string;
  level: string;
  part_code: string;
  part_title: string;
  item_type: string;
  scored: boolean;
  title: string;
  topic: string | null;
  payload: Record<string, unknown>;
}

export interface ReviewRow {
  id: string;
  correct: boolean;
  given: unknown;
  expected: unknown;
}

export interface SubmitResult {
  score_percent: number;
  correct: number;
  total: number;
  review: ReviewRow[];
  best_score_percent: number;
  attempts: number;
  /** The listening script — present only after answering, for review. */
  transcript?: string | null;
}

export function listExams(): Promise<ExamOverview[]> {
  return request("/exams/");
}

export function partItems(board: string, level: string, partCode: string): Promise<PartItems> {
  return request(`/exams/${board}/${level}/parts/${partCode}/items`);
}

export function examItem(itemId: string): Promise<ExamItemDetail> {
  return request(`/exams/items/${itemId}`);
}

export function submitExamItem(itemId: string, answers: Record<string, unknown>): Promise<SubmitResult> {
  return request(`/exams/items/${itemId}/submit`, { method: "POST", body: { answers } });
}

export interface Explanation {
  id: string;
  text: string;
}

export function explainItem(
  itemId: string,
  answers: Record<string, unknown>,
): Promise<{ configured: boolean; explanations: Explanation[] }> {
  return request(`/exams/items/${itemId}/explain`, { method: "POST", body: { answers } });
}

// ---- Mock exam (full timed practice test) ------------------------------

export interface MockExamItem {
  id: string;
  title: string;
  topic: string | null;
  payload: Record<string, unknown>;
}

export interface MockExamPart {
  code: string;
  title: string;
  item_type: string;
  timing_minutes: number;
  item: MockExamItem;
}

export interface MockExamSection {
  code: string;
  name: string;
  english: string;
  icon: string;
  parts: MockExamPart[];
}

export interface MockExam {
  board: string;
  level: string;
  name: string;
  total_minutes: number;
  item_ids: string[];
  sections: MockExamSection[];
}

export interface MockSectionResult {
  code: string;
  name: string;
  english: string;
  icon: string;
  correct: number;
  total: number;
  percent: number;
}

export interface MockItemResult {
  item_id: string;
  part_code: string;
  title: string;
  score_percent: number;
  review: ReviewRow[];
}

export interface MockResult {
  overall_percent: number;
  passed: boolean;
  total_correct: number;
  total_questions: number;
  sections: MockSectionResult[];
  items: MockItemResult[];
}

export interface MockAttempt {
  id: string;
  overall_percent: number;
  passed: boolean;
  total_correct: number;
  total_questions: number;
  taken_at: string | null;
}

export function getMockExam(board: string, level: string): Promise<MockExam> {
  return request(`/exams/${board}/${level}/mock`);
}

export function mockHistory(board: string, level: string): Promise<MockAttempt[]> {
  return request(`/exams/${board}/${level}/mock/history`);
}

export interface ReviewItem {
  id: string;
  slug: string;
  title: string;
  topic: string | null;
  part_code: string;
  best_score: number;
}

export function reviewQueue(board: string, level: string): Promise<{ items: ReviewItem[] }> {
  return request(`/exams/${board}/${level}/review`);
}

export function submitMockExam(
  board: string,
  level: string,
  answers: Record<string, Record<string, unknown>>,
): Promise<MockResult> {
  return request(`/exams/${board}/${level}/mock/submit`, { method: "POST", body: { answers } });
}

// ---- AI writing coach (Phase 5) ----------------------------------------

export interface WritingTaskPoint {
  point: string;
  addressed: boolean;
  comment: string;
}

export interface WritingCorrection {
  original: string;
  corrected: string;
  explanation: string;
}

export interface WritingFeedback {
  task_points: WritingTaskPoint[];
  content_score: number;
  organisation_score: number;
  language_score: number;
  score_percent: number;
  level_estimate: string;
  corrections: WritingCorrection[];
  strengths: string[];
  improvements: string[];
  overall_comment: string;
}

export interface WritingQuota {
  used: number;
  limit: number;
}

export interface WritingSubmission {
  id: string;
  created_at: string | null;
  word_count: number;
  score_percent: number;
  feedback: WritingFeedback;
}

export interface WritingSubmitResult extends WritingSubmission {
  best_score_percent: number;
  attempts: number;
  quota: WritingQuota;
}

export interface WritingHistory {
  configured: boolean;
  quota: WritingQuota | null;
  submissions: WritingSubmission[];
}

export function submitWriting(itemId: string, text: string): Promise<WritingSubmitResult> {
  return request(`/exams/items/${itemId}/writing`, { method: "POST", body: { text } });
}

export function writingHistory(itemId: string): Promise<WritingHistory> {
  return request(`/exams/items/${itemId}/writing`);
}

// ---- AI speaking partner (Phase 5b) ------------------------------------

export interface SpeakingFeedback {
  interaction_score: number;
  vocabulary_score: number;
  grammar_score: number;
  score_percent: number;
  level_estimate: string;
  corrections: WritingCorrection[];
  strengths: string[];
  improvements: string[];
  overall_comment: string;
}

export interface SpeakingTurn {
  role: "assistant" | "learner";
  text: string;
}

export interface SpeakingSession {
  id: string;
  created_at: string | null;
  status: string;
  turns: number;
  max_turns: number | null;
  score_percent: number | null;
  feedback: SpeakingFeedback | null;
  transcript: SpeakingTurn[];
}

export interface SpeakingStartResult extends SpeakingSession {
  reply: string;
  quota: WritingQuota;
}

export interface SpeakingReplyResult extends SpeakingSession {
  reply: string;
  ended: boolean;
}

export interface SpeakingEndResult extends SpeakingSession {
  ended: boolean;
}

export interface SpeakingHistory {
  configured: boolean;
  quota: WritingQuota | null;
  sessions: SpeakingSession[];
}

export function startSpeaking(itemId: string): Promise<SpeakingStartResult> {
  return request(`/exams/items/${itemId}/speaking/start`, { method: "POST" });
}

export function replySpeaking(sessionId: string, text: string): Promise<SpeakingReplyResult> {
  return request(`/exams/speaking/${sessionId}/reply`, { method: "POST", body: { text } });
}

export function endSpeaking(sessionId: string): Promise<SpeakingEndResult> {
  return request(`/exams/speaking/${sessionId}/end`, { method: "POST" });
}

export function speakingHistory(itemId: string): Promise<SpeakingHistory> {
  return request(`/exams/items/${itemId}/speaking`);
}

// ---- Free conversation (the standalone speaking bot) -------------------

export interface FreeChatOptions {
  language: string;
  /** null = "adapt to me": the server seeds difficulty from the learner's
   *  most recent CEFR read. */
  level: string | null;
  topic?: string;
}

export interface FreeChatStatus {
  configured: boolean;
  quota: WritingQuota | null;
  languages: { code: string; name: string }[];
  /** The level an adaptive chat would start at, from the learner's latest
   *  read; null until they've been graded at least once. */
  suggested_level?: string | null;
}

export function freeSpeakingStatus(): Promise<FreeChatStatus> {
  return request("/exams/speaking/free");
}

export function startFreeSpeaking(opts: FreeChatOptions): Promise<SpeakingStartResult> {
  return request("/exams/speaking/free/start", { method: "POST", body: opts });
}

/** A free conversation carries the settings it was started with, so a
 *  resumed one can say what it is. */
export interface FreeChatSession extends SpeakingSession {
  language: string | null;
  level: string | null;
  topic: string | null;
}

/** The caller's conversation that is still open, if any. A live chat used
 *  to exist only in this tab's memory; this is how a refresh finds it. */
export function activeFreeSpeaking(): Promise<{ session: FreeChatSession | null }> {
  return request("/exams/speaking/free/active");
}

/** Finished conversations, newest first, with their transcripts. */
export function freeSpeakingHistory(): Promise<{ sessions: FreeChatSession[] }> {
  return request("/exams/speaking/free/history");
}
